import React from 'react';
import styled from 'styled-components';

interface ButtonProps {
  variant?: 'primary' | 'outline' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  // Legacy support
  primary?: boolean;
}

const Button = styled.button<ButtonProps>`
  // Base styles
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  
  // Width styles
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  
  // Size styles
  ${props => {
    switch (props.size) {
      case 'sm':
        return `
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
        `;
      case 'lg':
        return `
          padding: 1.25rem 2.5rem;
          font-size: 1.125rem;
        `;
      default: // 'md'
        return `
          padding: 1rem 2rem;
          font-size: 1rem;
        `;
    }
  }}
  
  // Variant styles
  ${props => {
    const variant = props.variant || (props.primary ? 'primary' : 'secondary');
    
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
          color: white;
          
          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 10px 20px rgba(34, 197, 94, 0.3);
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.2);
          
          &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.1);
            border-color: rgba(255, 255, 255, 0.3);
            transform: translateY(-2px);
          }
        `;
      default: // 'secondary'
        return `
          background: rgba(255, 255, 255, 0.05);
          color: white;
          
          &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.1);
            transform: translateY(-2px);
          }
        `;
    }
  }}
  
  // Disabled state
  opacity: ${props => props.disabled ? 0.6 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

export default Button;
